"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";

export interface PopoverSelectOption {
  value: string;
  label: string;
}

interface PopoverSelectProps {
  /**
   * Forwarded to the trigger so a `<label htmlFor>` can point at it.
   *
   * The trigger is a button, not an input, so without this a label that used to be bound to
   * a native <select> silently stops being clickable — and stops being announced with the
   * control — the moment it is swapped for this component.
   */
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: PopoverSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Box styling for the trigger (border / padding / font). Text colour is handled internally. */
  triggerClassName?: string;
  contentClassName?: string;
  ariaLabel?: string;
  /** Optional: notified when the dropdown opens/closes (e.g. for analytics). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * A styled select built on Radix **Popover** (`modal={false}`) instead of Radix
 * Select.
 *
 * Radix Select always mounts `react-remove-scroll`, which locks WINDOW scroll
 * while the dropdown is open and exposes no prop to disable it — so on an
 * embedded form (e.g. Lead Drop 9 in Banner 10) the whole page can't scroll
 * while a dropdown is open. Popover with `modal={false}` does NOT lock scroll,
 * so the page (and the inline form) stay scrollable. The trigger + option list
 * are styled to match the previous Select dropdown look.
 */
export function PopoverSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  triggerClassName,
  contentClassName,
  ariaLabel,
  onOpenChange,
}: PopoverSelectProps) {
  const [open, setOpen] = React.useState(false);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const focusItem = (index: number) => {
    const n = options.length;
    if (n === 0) return;
    const i = ((index % n) + n) % n;
    itemRefs.current[i]?.focus();
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        onOpenChange?.(o);
      }}
      modal={false}
    >
      <PopoverPrimitive.Trigger
        id={id}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        className={cn("flex w-full items-center justify-between gap-2", triggerClassName)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() =>
              focusItem(
                e.key === "ArrowDown" ? Math.max(0, selectedIndex) : options.length - 1,
              ),
            );
          }
        }}
      >
        <span
          className={cn(
            "min-w-0 truncate text-left",
            selected ? "text-[#101112]" : "text-[#888]",
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className="size-4 shrink-0 text-[#888]"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          role="listbox"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            requestAnimationFrame(() => focusItem(selectedIndex >= 0 ? selectedIndex : 0));
          }}
          className={cn(
            "z-[200] max-h-[280px] w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-lg border border-[#ededed] bg-white p-1 shadow-lg",
            contentClassName,
          )}
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onValueChange(opt.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    focusItem(i + 1);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    focusItem(i - 1);
                  }
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center justify-between rounded-[6px] px-3 py-2 text-left text-[#101112] outline-none hover:bg-[#f5f5f5] focus:bg-[#f5f5f5]",
                  isSelected && "bg-[#f5f5f5] font-medium",
                )}
              >
                <span className="min-w-0 truncate">{opt.label}</span>
                {isSelected && (
                  <svg
                    className="ml-2 size-4 shrink-0 text-[#9F1C34]"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
